/**
 * Maps Cube connector/iframe events to widget calling state (phone, name, Status).
 * Used by syncCubeCallState and case timeline enrichment.
 */
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CallingService } from '../calling.service';

export interface CubeCallDisplayState {
  dueId?: number | string;
  phoneNo?: string;
  phoneNumber?: string;
  fullName?: string;
  Status?: string;
  clientId?: string;
  callingType: 'cube';
  pauseCall?: boolean;
  holdCall?: boolean;
  isToggled?: boolean;
  breakValue?: string;
  customerAnswered?: boolean;
  callDirection?: 'campaign' | 'manual' | 'inbound';
  [key: string]: any;
}

export type CubeCallUiState = CubeCallDisplayState;

const PHONE_FIELD_KEYS = [
  'phoneNo',
  'phoneNumber',
  'phoneno',
  'PhoneNo',
  'Phoneno',
  'number',
  'callerID',
  'callerId',
  'callerNumber',
  'CallerNumber',
  'cli',
  'CLI',
  'customerNumber',
  'CustomerNumber',
  'customerPhone',
  'CustomerPhone',
  'mobileNum',
  'mobileNumber',
  'mobile',
  'Mobile',
  'ani',
  'ANI',
  'remoteNumber',
  'RemoteNumber',
  'from',
  'From',
  'dialedNumber',
  'DialedNumber',
  'contactNumber',
  'ContactNumber',
];

@Injectable({ providedIn: 'root' })
export class CubeDialerFlowService {
  private lastNavigationKey = '';
  private lastNavigationAt = 0;
  private lookupInFlight = new Map<string, Promise<CubeCallDisplayState | null>>();

  constructor(
    private callingService: CallingService,
    private http: HttpClient,
    private router: Router
  ) {}

  mapActivity(activity: string): string {
    const value = String(activity || '').toLowerCase();
    if (value.includes('break')) return 'PAUSED';
    if (value.includes('busy') || value.includes('talk') || value.includes('call')) return 'BUSY';
    if (value.includes('not') && value.includes('ready')) return 'NOT_READY';
    return 'IDLE';
  }

  refreshAgentStatus(): Observable<string> {
    return this.callingService.getCubeAgentStatus().pipe(
      map((res: any) => {
        if (res?.resultShort === 'Success') {
          return this.mapActivity(res.status || res.data?.Activity);
        }
        return 'IDLE';
      })
    );
  }

  getPhoneNumber(data: any): string {
    if (!data) {
      return '';
    }
    if (typeof data === 'string') {
      return this.normalizePhoneDigits(data);
    }
    const roots = [
      data?.payload,
      data?.data,
      data?.callData,
      data?.customer,
      data?.caller,
      data,
    ];
    for (const root of roots) {
      const phone = this.readPhoneFromObject(root);
      if (phone) {
        return phone;
      }
    }
    return this.findPhoneInTree(data, new Set(), 0);
  }

  getClientId(data: any): string {
    const root = data?.payload && typeof data.payload === 'object' ? data.payload : data;
    const clientId =
      root?.clientId ||
      root?.clientid ||
      root?.ClientID ||
      root?.ClientId ||
      data?.clientId ||
      data?.clientid;
    return clientId != null ? String(clientId) : '';
  }

  resolveDueId(data: any): number | string | undefined {
    const raw = data?.dueId ?? data?.DueId;
    const sanitized = this.sanitizeDueId(raw);
    if (sanitized) {
      return sanitized;
    }
    const clientId = this.getClientId(data);
    if (!clientId || !clientId.includes(':')) {
      return undefined;
    }
    const part = clientId.split(':').pop();
    return this.sanitizeDueId(part);
  }

  sanitizeDueId(value: unknown): string | undefined {
    if (value == null || value === '') {
      return undefined;
    }
    const raw = decodeURIComponent(String(value)).split(/[?&#]/)[0].trim();
    const digits = raw.replace(/\D/g, '');
    return digits && /^\d+$/.test(digits) ? digits : undefined;
  }

  resolveFullName(data: any): string {
    const root = data?.payload && typeof data.payload === 'object' ? data.payload : data;
    const name =
      root?.fullName ||
      root?.name ||
      root?.Name ||
      root?.customerName ||
      root?.custName ||
      root?.callerName ||
      root?.CallerName ||
      data?.fullName ||
      data?.name ||
      data?.Name;
    let resolved = name != null ? String(name).trim() : '';
    if (resolved.includes('%')) {
      try {
        resolved = decodeURIComponent(resolved).trim();
      } catch (_e) {}
    }
    if (!resolved || /^unknown$/i.test(resolved)) {
      return '';
    }
    return resolved;
  }

  enrichCallDisplayData(data: any): CubeCallDisplayState {
    if (!data || typeof data !== 'object') {
      return { callingType: 'cube' };
    }
    const phoneNo = this.getPhoneNumber(data);
    const clientId = this.getClientId(data);
    const dueId = this.resolveDueId(data);
    const fullName = this.resolveFullName(data);
    const out: CubeCallDisplayState = {
      ...data,
      callingType: 'cube',
    };
    if (phoneNo) {
      out.phoneNo = phoneNo;
      out.phoneNumber = phoneNo;
    }
    if (clientId) {
      out.clientId = clientId;
    }
    if (dueId) {
      out.dueId = dueId;
      out['DueId'] = dueId;
    }
    if (fullName) {
      out.fullName = fullName;
    }
    return out;
  }

  getCallKey(data: any): string {
    return [
      data?.dueId || data?.DueId || '',
      this.getClientId(data),
      this.getPhoneNumber(data),
    ].join(':');
  }

  normalizeCallData(data: CubeCallDisplayState, fallbackStatus = 'CONNECTED'): CubeCallDisplayState {
    const enriched = this.enrichCallDisplayData(data);
    const status = this.normalizeStatus(enriched?.Status || enriched?.['status'], fallbackStatus);
    const dueId = this.sanitizeDueId(enriched?.dueId || enriched?.['DueId'] || this.resolveDueId(enriched));
    const phoneNo = this.getPhoneNumber(enriched);
    const clientId = this.getClientId(enriched);
    const fullName = this.resolveFullName(enriched);
    const out: CubeCallDisplayState = {
      ...enriched,
      dueId,
      ['DueId']: dueId,
      phoneNo,
      phoneNumber: enriched?.phoneNumber || phoneNo,
      clientId: clientId || enriched?.clientId,
      Status: status,
      status,
      callingType: 'cube',
    };
    if (fullName) {
      out.fullName = fullName;
    } else {
      delete out.fullName;
    }
    return out;
  }

  async lookupCase(
    callData: CubeCallDisplayState,
    fallbackStatus = 'CONNECTED'
  ): Promise<CubeCallDisplayState | null> {
    const key = `${this.getCallKey(callData)}|${fallbackStatus}`;
    if (key && this.lookupInFlight.has(key)) {
      return this.lookupInFlight.get(key)!;
    }
    const request = this.fetchLookupCase(callData, fallbackStatus);
    if (key) {
      this.lookupInFlight.set(key, request);
      request.finally(() => {
        if (this.lookupInFlight.get(key) === request) {
          this.lookupInFlight.delete(key);
        }
      });
    }
    return request;
  }

  private async fetchLookupCase(
    callData: CubeCallDisplayState,
    fallbackStatus = 'CONNECTED'
  ): Promise<CubeCallDisplayState | null> {
    const phoneNumber = this.getPhoneNumber(callData);
    const clientId = this.getClientId(callData);
    const dueId = callData?.dueId || callData?.['DueId'] || this.resolveDueId(callData);
    const response: any = await firstValueFrom(
      this.http.post(
        `${environment.api_url}/api/ozonetel-webhook/lookup-case`,
        {
          ...(phoneNumber ? { phoneNumber } : {}),
          ...(clientId ? { clientId } : {}),
          ...(dueId ? { dueId } : {}),
        },
        { withCredentials: true }
      )
    );
    if (response?.resultShort === 'success' && response.data) {
      const existingName = this.resolveFullName(callData);
      const lookupName = this.resolveFullName(response.data);
      const fullName = lookupName || existingName || this.resolveFullName(callData) || '';
      return this.normalizeCallData(
        {
          ...callData,
          ...response.data,
          ...(fullName ? { fullName } : {}),
          Status: fallbackStatus,
        },
        fallbackStatus
      );
    }
    if (phoneNumber) {
      const existingName = this.resolveFullName(callData);
      return this.normalizeCallData(
        {
          ...callData,
          phoneNo: phoneNumber,
          phoneNumber,
          ...(existingName ? { fullName: existingName } : { fullName: '' }),
          dueId: callData?.dueId || callData?.['DueId'],
          clientId: callData?.clientId || '',
          Status: fallbackStatus,
        },
        fallbackStatus
      );
    }
    return null;
  }

  async openCaseFromCall(
    callData: CubeCallDisplayState,
    fallbackStatus = 'CONNECTED'
  ): Promise<CubeCallDisplayState | null> {
    const callStatus = this.normalizeStatus(
      callData?.Status || callData?.['status'] || fallbackStatus,
      fallbackStatus
    );

    let normalized = this.normalizeCallData(
      {
        ...callData,
        Status: callStatus,
      },
      callStatus
    );

    if (!normalized.phoneNo || !normalized.fullName || !normalized.dueId) {
      const lookupResult = await this.lookupCase(normalized, callStatus);
      if (lookupResult) {
        normalized = this.normalizeCallData(lookupResult, callStatus);
      }
    }

    if (!normalized.phoneNo && !normalized.dueId) {
      return null;
    }

    const shouldOpenTimeline =
      callData.callDirection === 'campaign'
        ? !!callData.customerAnswered && this.isCustomerAnswered(callStatus)
        : this.isCustomerAnswered(callStatus);

    if (shouldOpenTimeline && normalized.dueId) {
      this.openCase(normalized);
    }

    return normalized;
  }

  openCase(callData: CubeCallDisplayState): boolean {
    const normalized = this.normalizeCallData(callData, callData?.Status || 'CONNECTED');
    const dueId = this.sanitizeDueId(normalized.dueId ?? normalized['DueId']);
    if (!dueId || !this.isOpenCaseStatus(normalized.Status, normalized.customerAnswered)) {
      return false;
    }
    if (normalized.Status === 'CALLING') {
      return false;
    }
    normalized.dueId = dueId;
    normalized['DueId'] = dueId;
    const navigationKey = this.getCallKey(normalized);
    const now = Date.now();
    if (navigationKey && navigationKey === this.lastNavigationKey && now - this.lastNavigationAt < 1500) {
      return true;
    }
    this.lastNavigationKey = navigationKey;
    this.lastNavigationAt = now;
    try {
      localStorage.setItem('callingType', 'cube');
      localStorage.setItem('incomingCall', 'true');
    } catch (_e) {}
    const fullName = this.resolveFullName(normalized);
    const phoneNo = this.getPhoneNumber(normalized) || '';
    const queryParams: Record<string, string> = {
      callerNo: phoneNo,
      phoneNo,
      callingType: 'cube',
    };
    if (fullName) {
      queryParams['fullName'] = fullName;
    }
    const clientId = normalized.clientId ? String(normalized.clientId) : '';
    if (clientId) {
      queryParams['clientId'] = clientId;
    }
    void this.router.navigate(['/timeline', dueId], {
      queryParams,
      replaceUrl: true,
    });
    return true;
  }

  parseSocketMessage(message: unknown): CubeCallDisplayState {
    return this.enrichCallDisplayData(typeof message === 'string' ? JSON.parse(message) : message);
  }

  toCallingState(data: Record<string, any>, status?: string): CubeCallDisplayState {
    return this.normalizeCallData(
      {
        ...data,
        callingType: 'cube',
        Status: status || data?.['Status'] || data?.['status'] || 'Idle',
      },
      status || 'Idle'
    );
  }

  private readPhoneFromObject(obj: any): string {
    if (!obj || typeof obj !== 'object') {
      return '';
    }
    for (const key of PHONE_FIELD_KEYS) {
      const value = obj[key];
      if (value == null || value === '') {
        continue;
      }
      const phone = this.normalizePhoneDigits(String(value));
      if (phone) {
        return phone;
      }
    }
    return '';
  }

  private findPhoneInTree(value: any, seen: Set<any>, depth: number): string {
    if (value == null || depth > 5) {
      return '';
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return this.normalizePhoneDigits(String(value));
    }
    if (typeof value !== 'object') {
      return '';
    }
    if (seen.has(value)) {
      return '';
    }
    seen.add(value);
    const direct = this.readPhoneFromObject(value);
    if (direct) {
      return direct;
    }
    for (const key of Object.keys(value)) {
      if (key === 'password' || key === 'Password' || key === 'token' || key === 'Token') {
        continue;
      }
      const found = this.findPhoneInTree(value[key], seen, depth + 1);
      if (found) {
        return found;
      }
    }
    return '';
  }

  private normalizePhoneDigits(raw: string): string {
    const digits = String(raw || '').replace(/\D/g, '');
    if (digits.length < 10) {
      return '';
    }
    return digits.length > 10 ? digits.slice(-10) : digits;
  }

  private normalizeStatus(status: any, fallbackStatus: string): string {
    const value = String(status || fallbackStatus || '').toUpperCase();
    if (['CONNECTED', 'BUSY', 'ANSWERED', 'LIVE'].includes(value)) {
      return 'CONNECTED';
    }
    if (['CALLING', 'RINGING', 'NEW_CALL', 'INITIATED', 'QUEUED', 'INBOUND_CALL'].includes(value)) {
      return 'CALLING';
    }
    if (['DISCONNECTED', 'DROP', 'DROPPED', 'ENDED', 'COMPLETED'].includes(value)) {
      return 'Disconnected';
    }
    if (value === 'IDLE') {
      return 'Idle';
    }
    return fallbackStatus || 'CONNECTED';
  }

  private isCustomerAnswered(status: string): boolean {
    return this.normalizeStatus(status, 'CALLING') === 'CONNECTED';
  }

  private isOpenCaseStatus(status: string, customerAnswered?: boolean): boolean {
    return this.isCustomerAnswered(status);
  }
}
